import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft,
  Search,
  MapPin,
  Phone,
  Calendar,
  Filter,
  AlertCircle,
  Building2,
  Download,
  Star,
  Clock,
  Navigation,
  Sparkles,
  Route,
  ShieldCheck,
  SearchCheck
} from 'lucide-react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { haversineKm, getPharmacyCoords, buildDirectionsUrl, isHomePharmacy } from '../../utils/gardeGeo';
import { isCurrentWeek, sortWeeksChronologically } from '../../utils/gardeWeek';
import SeoHead from '../../components/seo/SeoHead';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// The home pharmacy we want to highlight when on duty
const HOME_PHARMACY_NAME_MATCH = /SAINT\s*ANGE\s*DES\s*AMIS/i;

const PharmacyGardePage = ({ onBack, onNavigate }) => {
  const [gardeData, setGardeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');
  const [sortByNearest, setSortByNearest] = useState(false);
  const [directionsWarning, setDirectionsWarning] = useState(null); // pharmacy being confirmed

  const logoUrl = 'https://customer-assets.emergentagent.com/job_skincare-check-1/artifacts/6cemh04i_LOGO%20DEF.jpg';

  const fetchGardeData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/pharmacy/garde`);
      setGardeData(response.data);
      setError(null);
      // Auto-select current week if available
      if (response.data?.pharmacies?.length > 0) {
        const weeks = [...new Set(response.data.pharmacies.map(p => p.week).filter(Boolean))];
        if (weeks.length > 0 && !selectedWeek) {
          // Try to find current week based on today's date
          const today = new Date();
          const currentDay = today.getDate();
          const currentMonth = today.toLocaleString('fr-FR', { month: 'long' }).toUpperCase();
          
          // Find week that contains current date
          const currentWeek = weeks.find(week => {
            const match = week.match(/(\d{2}).*?AU.*?(\d{2})/i);
            if (match) {
              const startDay = parseInt(match[1]);
              const endDay = parseInt(match[2]);
              // Simple check: current day is between start and end
              // This works for most cases within the same month
              if (week.toUpperCase().includes(currentMonth) || week.toUpperCase().includes('MARS') || week.toUpperCase().includes('AVRIL')) {
                return currentDay >= startDay && currentDay <= endDay;
              }
            }
            return false;
          });
          
          // Select current week or last week (most recent)
          setSelectedWeek(currentWeek || weeks[weeks.length - 1]);
        }
      }
    } catch (err) {
      console.error('Error fetching garde data:', err);
      setError('Impossible de charger les pharmacies de garde');
    } finally {
      setLoading(false);
    }
  }, [selectedWeek]);

  useEffect(() => {
    fetchGardeData();
  }, [fetchGardeData]);

  // Get unique weeks for filter - sorted chronologically
  const weeks = gardeData?.pharmacies
    ? [...new Set(gardeData.pharmacies.map(p => p.week).filter(Boolean))].sort((a, b) => {
        // Extract the start date from the week string
        const extractDate = (week) => {
          const match = week.match(/(\d{2})(?:\s+(?:MARS|AVRIL|MAI|JUIN))?/i);
          return match ? parseInt(match[1]) : 0;
        };
        return extractDate(a) - extractDate(b);
      })
    : [];

  // Get unique sections (main sections) for filter
  const sections = gardeData?.pharmacies
    ? [...new Set(gardeData.pharmacies.map(p => p.section).filter(Boolean))].sort()
    : [];

  // Filter pharmacies
  const filteredPharmacies = gardeData?.pharmacies?.filter(pharmacy => {
    const matchesSearch = !searchQuery ||
      pharmacy.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pharmacy.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pharmacy.manager?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pharmacy.section?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pharmacy.sub_section?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSection = !selectedSection || pharmacy.section === selectedSection;
    const matchesWeek = !selectedWeek || pharmacy.week === selectedWeek;

    return matchesSearch && matchesSection && matchesWeek;
  }) || [];

  // Enrich with distance if user location known
  const enrichedPharmacies = filteredPharmacies.map((p) => {
    if (!userLocation) return { ...p, _distanceKm: null };
    const coords = getPharmacyCoords(p);
    const d = coords ? haversineKm(userLocation, coords) : null;
    return { ...p, _distanceKm: d };
  });

  // Sort by distance when requested
  const sortedPharmacies = sortByNearest && userLocation
    ? [...enrichedPharmacies].sort((a, b) => {
        const da = a._distanceKm ?? Infinity;
        const db = b._distanceKm ?? Infinity;
        return da - db;
      })
    : enrichedPharmacies;

  // Detect "home pharmacy" on duty for selected week
  const homePharmacyOnDuty = gardeData?.pharmacies?.find(p =>
    HOME_PHARMACY_NAME_MATCH.test(p.name || '') &&
    (!selectedWeek || p.week === selectedWeek)
  );

  // Handle geolocation request
  const requestGeolocation = () => {
    if (!navigator.geolocation) {
      setGeoError('La géolocalisation n\'est pas disponible sur ce navigateur.');
      return;
    }
    setGeoLoading(true);
    setGeoError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation([pos.coords.latitude, pos.coords.longitude]);
        setSortByNearest(true);
        setGeoLoading(false);
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGeoError('Autorisation refusée. Activez la localisation dans votre navigateur.');
        } else {
          setGeoError('Impossible d\'obtenir votre position. Réessayez.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Build Google Maps directions URL and open in new tab
  const openDirections = (pharmacy) => {
    // Home pharmacy always has accurate coords — skip warning
    if (isHomePharmacy(pharmacy)) {
      _openDirectionsUrl(pharmacy);
      return;
    }
    const hasGps = Number.isFinite(pharmacy?.lat) && Number.isFinite(pharmacy?.lng);
    // If no precise GPS, show a confirmation dialog FIRST
    if (!hasGps) {
      setDirectionsWarning(pharmacy);
      return;
    }
    _openDirectionsUrl(pharmacy);
  };

  const _openDirectionsUrl = (pharmacy) => {
    const { url } = buildDirectionsUrl(pharmacy, userLocation);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const confirmDirections = () => {
    if (directionsWarning) {
      _openDirectionsUrl(directionsWarning);
      setDirectionsWarning(null);
    }
  };

  // Generate PDF function
  const generatePDF = () => {
    const doc = new jsPDF();
    const pharmacies = gardeData?.pharmacies || [];
    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(93, 78, 55); // #5D4E37
    doc.text('PHARMACIES DE GARDE', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(196, 92, 38); // #C45C26
    doc.text(gardeData?.month || 'Mars 2026', 105, 28, { align: 'center' });
    
    // Group by week
    const byWeek = {};
    pharmacies.forEach(p => {
      const week = p.week || 'Non spécifié';
      if (!byWeek[week]) byWeek[week] = [];
      byWeek[week].push(p);
    });
    
    let yPos = 40;
    
    Object.keys(byWeek).sort().forEach(week => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      // Week header
      doc.setFontSize(11);
      doc.setTextColor(196, 92, 38);
      doc.text(week, 14, yPos);
      yPos += 6;
      
      // Table data
      const tableData = byWeek[week].map(p => [
        p.name || '',
        p.section || p.city || '',
        (p.phones || []).slice(0, 2).join(' / ')
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [['Pharmacie', 'Secteur', 'Téléphone']],
        body: tableData,
        theme: 'grid',
        headStyles: { 
          fillColor: [93, 78, 55],
          textColor: 255,
          fontSize: 9
        },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 60 },
          2: { cellWidth: 60 }
        },
        margin: { left: 14, right: 14 }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Pharmacie Saint Ange Des Amis - Page ${i}/${pageCount}`,
        105, 290, { align: 'center' }
      );
    }
    
    doc.save(`pharmacies_garde_${gardeData?.month?.replace(/\s+/g, '_') || 'export'}.pdf`);
  };

  // Group by section > sub_section for hierarchical display
  // When sorting by nearest, we flatten instead of grouping
  const groupedBySection = sortedPharmacies.reduce((acc, pharmacy) => {
    const section = pharmacy.section || 'Autres';
    const sub = pharmacy.sub_section || '';
    if (!acc[section]) acc[section] = {};
    if (!acc[section][sub]) acc[section][sub] = [];
    acc[section][sub].push(pharmacy);
    return acc;
  }, {});

  // Top nearest list (when user location known and sortByNearest active)
  const nearestList = sortByNearest && userLocation
    ? sortedPharmacies.filter(p => p._distanceKm !== null).slice(0, 6)
    : [];

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedSection('');
    setSelectedWeek('');
  };

  const hasActiveFilters = searchQuery || selectedSection || selectedWeek;

  // Format week for display (shorter version)
  const formatWeek = (week) => {
    if (!week) return '';
    // "SEMAINE DU SAMEDI 07 AU VENDREDI 13 MARS 2026" -> "07 - 13 Mars"
    const match = week.match(/(\d{2}).*?(\d{2})\s*(MARS|AVRIL|MAI|JUIN|JUILLET)/i);
    if (match) {
      return `${match[1]} - ${match[2]} ${match[3].charAt(0) + match[3].slice(1).toLowerCase()}`;
    }
    return week;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF8F3] to-white">
      <SeoHead
        title="Pharmacie de garde Abidjan — Cocody, Plateau, Angré, Yopougon, Abobo, Treichville"
        description="Liste à jour des pharmacies de garde à Abidjan : Cocody, Plateau, Angré 7ème Tranche, Yopougon, Abobo, Treichville, Marcory, Port-Bouët, Adjamé, Koumassi. Numéros de téléphone, adresses, itinéraire Google Maps en un clic, recherche de la pharmacie la plus proche par géolocalisation."
        canonical="/pharmacie-de-garde-abidjan"
      />
      {/* Header - Same as PharmacyHomePage */}
      <header className="bg-white/90 backdrop-blur-md border-b border-[#E8DDD4] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                className="text-[#8B7355] hover:bg-[#F5EDE4]"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img 
                src={logoUrl} 
                alt="Pharmacie Saint Ange Des Amis" 
                className="h-10 w-auto object-contain cursor-pointer"
                onClick={() => onNavigate('home')}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => onNavigate('staff-login')}
              className="border-[#C45C26] text-[#C45C26] hover:bg-[#C45C26] hover:text-white px-2.5 sm:px-3 py-2 text-xs sm:text-sm whitespace-nowrap"
              data-testid="staff-login-btn"
            >
              Espace Conseiller
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Terracotta style */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FDF8F3] to-[#F5EDE4]">
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-[#C45C26]/10 text-[#C45C26] border-[#C45C26]/20 mb-4">
              <Calendar className="w-3 h-3 mr-1" />
              {gardeData?.month || 'Pharmacies de Garde'}
            </Badge>
            <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
              <span className="text-[#C45C26]">Pharmacies</span><br />
              <span className="text-[#5D4E37]">de Garde - Abidjan</span>
            </h1>
            <p className="text-[#8B7355] text-lg mb-8">
              Trouvez une pharmacie ouverte la nuit ou le week-end près de chez vous
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto mb-6">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#B8A089]" />
              <Input
                type="text"
                placeholder="Rechercher une pharmacie..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-lg rounded-2xl border-2 border-[#E8DDD4] focus:border-[#C45C26] bg-white shadow-lg"
              />
            </div>

            {/* Nearest pharmacy CTA */}
            <div className="flex flex-col items-center gap-2 mb-6">
              {!sortByNearest ? (
                <Button
                  onClick={requestGeolocation}
                  disabled={geoLoading}
                  data-testid="find-nearest-btn"
                  className="bg-gradient-to-r from-[#2E7D32] to-[#1B5E20] hover:from-[#1B5E20] hover:to-[#154A18] text-white shadow-lg rounded-full px-6 py-5 text-base font-semibold"
                >
                  <Navigation className={`w-5 h-5 mr-2 ${geoLoading ? 'animate-pulse' : ''}`} />
                  {geoLoading
                    ? 'Localisation en cours…'
                    : 'Trouver la pharmacie la plus proche'}
                </Button>
              ) : (
                <div className="flex items-center gap-3">
                  <Badge className="bg-[#2E7D32]/10 text-[#2E7D32] border-[#2E7D32]/20 px-3 py-1.5">
                    <Navigation className="w-3.5 h-3.5 mr-1.5" />
                    Tri par proximité activé
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSortByNearest(false);
                      setUserLocation(null);
                    }}
                    className="text-[#8B7355] hover:text-[#5D4E37]"
                    data-testid="clear-nearest-btn"
                  >
                    Désactiver
                  </Button>
                </div>
              )}
              {geoError && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {geoError}
                </p>
              )}
            </div>

            {/* Week Filter - Prominent */}
            {weeks.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-[#C45C26]" />
                  <span className="font-semibold text-[#5D4E37]">Semaine de garde</span>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {weeks.map(week => (
                    <button
                      key={week}
                      type="button"
                      onClick={() => setSelectedWeek(selectedWeek === week ? '' : week)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-150 touch-manipulation active:scale-[0.97] ${
                        selectedWeek === week
                          ? 'bg-[#C45C26] text-white shadow-lg'
                          : 'bg-white text-[#5D4E37] border-2 border-[#E8DDD4] hover:border-[#C45C26]'
                      }`}
                    >
                      {formatWeek(week)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Section Filter — chips for reliable mobile taps */}
            {sections.length > 0 && (
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Filter className="w-5 h-5 text-[#8B7355]" />
                  <span className="font-semibold text-[#5D4E37]">Section</span>
                </div>
                <div className="flex flex-wrap justify-center gap-2" data-testid="garde-section-chips">
                  <button
                    type="button"
                    onClick={() => setSelectedSection('')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-150 touch-manipulation active:scale-[0.97] ${
                      selectedSection === ''
                        ? 'bg-[#C45C26] text-white shadow-lg'
                        : 'bg-white text-[#5D4E37] border-2 border-[#E8DDD4] hover:border-[#C45C26]'
                    }`}
                    data-testid="section-chip-all"
                  >
                    Toutes
                  </button>
                  {sections.map(section => {
                    const label = section.replace(/^SECTION\s+(D'|DE\s+|DU\s+)/i, '').trim() || section;
                    const isActive = selectedSection === section;
                    return (
                      <button
                        key={section}
                        type="button"
                        onClick={() => setSelectedSection(isActive ? '' : section)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-150 touch-manipulation active:scale-[0.97] ${
                          isActive
                            ? 'bg-[#C45C26] text-white shadow-lg'
                            : 'bg-white text-[#5D4E37] border-2 border-[#E8DDD4] hover:border-[#C45C26]'
                        }`}
                        data-testid={`section-chip-${label}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="px-4 py-2 rounded-full text-sm font-medium text-[#C45C26] hover:bg-[#C45C26]/10 transition-colors touch-manipulation"
                    >
                      Effacer
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-[#C45C26]/10 rounded-full blur-2xl" />
        <div className="absolute bottom-10 right-20 w-32 h-32 bg-[#2E7D32]/10 rounded-full blur-3xl" />
      </section>

      {/* Emergency Numbers Card */}
      <section className="max-w-7xl mx-auto px-4 -mt-6 relative z-10 space-y-4">
        {/* Home pharmacy on-duty banner */}
        {homePharmacyOnDuty && (
          <Card
            className="bg-gradient-to-r from-[#FF6B35] via-[#E63946] to-[#D62828] border-0 shadow-2xl overflow-hidden relative"
            data-testid="home-pharmacy-banner"
          >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,215,0,0.25),_transparent_60%)]" />
            <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-[#FFD60A]/20 rounded-full blur-3xl" />
            <CardContent className="p-5 md:p-6 relative">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl ring-4 ring-white/40">
                    <img
                      src="https://customer-assets.emergentagent.com/job_654c116d-7b3e-48f0-8086-839f11b37e43/artifacts/t633m3qr_image.png"
                      alt="Ouvert 24 heures"
                      className="w-16 h-16 object-contain"
                    />
                  </div>
                  <div>
                    <Badge className="bg-[#FFD60A] text-[#D62828] hover:bg-[#FFD60A] font-black mb-1 border-0 uppercase tracking-wider text-xs shadow-md">
                      Nous sommes de garde
                    </Badge>
                    <p className="text-white/95 text-xs font-medium">
                      {homePharmacyOnDuty.week}
                    </p>
                  </div>
                </div>
                <div className="flex-1 text-white">
                  <h3 className="text-xl md:text-2xl font-bold leading-tight drop-shadow">
                    {homePharmacyOnDuty.name}
                  </h3>
                  <p className="text-white/95 text-sm mt-1 flex items-start gap-1.5">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      {homePharmacyOnDuty.section?.replace(/^SECTION\s+(D'|DE\s+|DU\s+)/i, '').trim()}
                      {homePharmacyOnDuty.sub_section && ` › ${homePharmacyOnDuty.sub_section}`}
                      {homePharmacyOnDuty.location && ` — ${homePharmacyOnDuty.location}`}
                    </span>
                  </p>
                </div>
                {homePharmacyOnDuty.phones?.length > 0 && (
                  <div className="flex flex-wrap gap-2 md:flex-shrink-0">
                    {homePharmacyOnDuty.phones.slice(0, 2).map((phone, i) => (
                      <a
                        key={i}
                        href={`tel:${phone.replace(/\s/g, '')}`}
                        className="inline-flex items-center gap-2 bg-white text-[#D62828] font-bold px-4 py-2 rounded-full hover:bg-[#FFD60A] hover:text-[#D62828] transition-colors shadow-lg"
                        data-testid="home-pharmacy-call"
                      >
                        <Phone className="w-4 h-4" />
                        {phone}
                      </a>
                    ))}
                    <button
                      onClick={() => openDirections(homePharmacyOnDuty)}
                      className="inline-flex items-center gap-2 bg-[#1A1A1A]/80 backdrop-blur text-white font-bold px-4 py-2 rounded-full hover:bg-black transition-colors border border-white/40 shadow-lg"
                      data-testid="home-pharmacy-directions"
                    >
                      <Route className="w-4 h-4" />
                      Itinéraire
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-red-800">Urgences :</span>
              </div>
              <a href="tel:185" className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors">
                <Phone className="w-4 h-4" />
                SAMU : 185
              </a>
              <a href="tel:180" className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 transition-colors">
                <Phone className="w-4 h-4" />
                Pompiers : 180
              </a>
              <a href="tel:170" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors">
                <Phone className="w-4 h-4" />
                Police : 170
              </a>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        {/* Results Count */}
        {!loading && gardeData && (
          <p className="text-[#8B7355] mb-6 text-center">
            <span className="font-semibold text-[#C45C26]">{filteredPharmacies.length}</span> pharmacie{filteredPharmacies.length > 1 ? 's' : ''} trouvée{filteredPharmacies.length > 1 ? 's' : ''}
          </p>
        )}

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="animate-pulse border-[#E8DDD4]">
                <CardContent className="p-4">
                  <div className="h-5 bg-[#E8DDD4] rounded w-3/4 mb-2" />
                  <div className="h-4 bg-[#E8DDD4] rounded w-1/2 mb-2" />
                  <div className="h-4 bg-[#E8DDD4] rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-6 text-center">
              <AlertCircle className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <p className="text-amber-800 font-medium mb-2">{error}</p>
              <p className="text-amber-700 text-sm mb-4">
                Les données des pharmacies de garde ne sont pas encore disponibles pour ce mois.
              </p>
              <Button onClick={fetchGardeData} className="bg-[#C45C26] hover:bg-[#A84B1F]">
                Réessayer
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!loading && !error && filteredPharmacies.length === 0 && (
          <Card className="border-[#E8DDD4]">
            <CardContent className="p-6 text-center">
              <Building2 className="w-12 h-12 text-[#8B7355]/30 mx-auto mb-4" />
              <p className="text-[#8B7355]">Aucune pharmacie trouvée avec ces critères</p>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-[#C45C26] mt-2"
                >
                  Effacer les filtres
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pharmacies List */}
        {!loading && !error && filteredPharmacies.length > 0 && (
          <div className="space-y-10">
            {/* Top nearest pharmacies (when user location is known) */}
            {nearestList.length > 0 && (
              <div data-testid="nearest-list">
                <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-[#2E7D32]/30">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#2E7D32] to-[#1B5E20] rounded-2xl flex items-center justify-center shadow-md">
                    <Navigation className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-wider text-[#2E7D32] font-semibold">Près de vous</p>
                    <h2 className="text-2xl font-bold text-[#5D4E37]">Pharmacies les plus proches</h2>
                  </div>
                  <Badge className="bg-[#2E7D32]/10 text-[#2E7D32] border-0 text-sm px-3 py-1">
                    Top {nearestList.length}
                  </Badge>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {nearestList.map((pharmacy, idx) => (
                    <Card
                      key={`near-${pharmacy.name}-${idx}`}
                      className="border-[#2E7D32]/30 bg-gradient-to-br from-white to-[#F0F9F0] hover:shadow-lg transition-all"
                      data-testid="nearest-pharmacy-card"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <h4 className="font-semibold text-[#5D4E37] text-lg flex-1">
                            {pharmacy.name}
                          </h4>
                          <Badge className="bg-[#2E7D32] text-white border-0 text-xs shrink-0">
                            {pharmacy._distanceKm < 1
                              ? `${Math.round(pharmacy._distanceKm * 1000)} m`
                              : `${pharmacy._distanceKm.toFixed(1)} km`}
                          </Badge>
                        </div>
                        <p className="text-xs text-[#2E7D32] font-medium mb-2">
                          {pharmacy.section?.replace(/^SECTION\s+(D'|DE\s+|DU\s+)/i, '').trim()}
                          {pharmacy.sub_section && ` › ${pharmacy.sub_section}`}
                        </p>
                        {pharmacy.location && (
                          <p className="text-sm text-[#5D4E37] flex items-start gap-2 mb-3">
                            <MapPin className="w-4 h-4 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                            <span>{pharmacy.location}</span>
                          </p>
                        )}
                        {pharmacy.phones?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {pharmacy.phones.slice(0, 2).map((phone, pIdx) => (
                              <a
                                key={pIdx}
                                href={`tel:${phone.replace(/\s/g, '')}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#2E7D32] text-white rounded-full text-sm font-medium hover:bg-[#1B5E20] transition-colors"
                              >
                                <Phone className="w-3 h-3" />
                                {phone}
                              </a>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#2E7D32]/10">
                          <Badge
                            variant="outline"
                            className={`text-[10px] px-2 py-0.5 gap-1 ${
                              pharmacy.geocoded
                                ? 'border-[#2E7D32]/40 text-[#2E7D32]'
                                : 'border-[#8B7355]/30 text-[#8B7355]'
                            }`}
                          >
                            {pharmacy.geocoded ? (
                              <><ShieldCheck className="w-3 h-3" /> GPS vérifié</>
                            ) : (
                              <><SearchCheck className="w-3 h-3" /> Recherche par nom</>
                            )}
                          </Badge>
                          <button
                            onClick={() => openDirections(pharmacy)}
                            className="inline-flex items-center gap-1 text-[#2E7D32] hover:text-[#1B5E20] font-semibold text-sm"
                            data-testid="directions-btn"
                          >
                            <Route className="w-4 h-4" />
                            Itinéraire
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {Object.entries(groupedBySection).map(([section, subSections]) => {
              const totalInSection = Object.values(subSections).reduce((s, arr) => s + arr.length, 0);
              // Friendly label: strip "SECTION D'" / "SECTION DE " / "SECTION DU "
              const sectionLabel = section
                .replace(/^SECTION\s+(D'|DE\s+|DU\s+)/i, '')
                .trim() || section;
              return (
                <div key={section} data-testid={`garde-section-${sectionLabel}`}>
                  <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-[#C45C26]/20">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#C45C26] to-[#A84B1F] rounded-2xl flex items-center justify-center shadow-md">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-wider text-[#8B7355] font-semibold">Section</p>
                      <h2 className="text-2xl font-bold text-[#5D4E37]">{sectionLabel}</h2>
                    </div>
                    <Badge className="bg-[#C45C26]/10 text-[#C45C26] border-0 text-sm px-3 py-1">
                      {totalInSection}
                    </Badge>
                  </div>

                  {Object.entries(subSections).map(([subName, pharmacies]) => (
                    <div key={`${section}-${subName}`} className="mb-6">
                      {subName && (
                        <div className="flex items-center gap-2 mb-3 ml-2">
                          <div className="w-1.5 h-6 bg-[#C45C26] rounded-full" />
                          <h3 className="text-base font-semibold text-[#C45C26] uppercase tracking-wide">
                            {subName}
                          </h3>
                          <Badge variant="outline" className="text-[#8B7355] border-[#E8DDD4] text-xs">
                            {pharmacies.length}
                          </Badge>
                        </div>
                      )}
                      <div className="grid md:grid-cols-2 gap-4">
                        {pharmacies.map((pharmacy, idx) => (
                          <Card
                            key={`${pharmacy.name}-${idx}`}
                            className="border-[#E8DDD4] hover:shadow-lg transition-all hover:border-[#C45C26]/30 bg-white"
                            data-testid="garde-pharmacy-card"
                          >
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <h4 className="font-semibold text-[#5D4E37] text-lg flex-1">
                                  {pharmacy.name}
                                </h4>
                                {pharmacy.secteur && (
                                  <Badge className="bg-[#2E7D32]/10 text-[#2E7D32] border-0 text-xs shrink-0">
                                    Secteur {pharmacy.secteur}
                                  </Badge>
                                )}
                              </div>
                              {pharmacy.manager && (
                                <p className="text-sm text-[#8B7355] mb-3">
                                  {pharmacy.manager}
                                </p>
                              )}
                              {pharmacy.location && (
                                <p className="text-sm text-[#5D4E37] flex items-start gap-2 mb-3">
                                  <MapPin className="w-4 h-4 text-[#C45C26] flex-shrink-0 mt-0.5" />
                                  <span>{pharmacy.location}</span>
                                </p>
                              )}
                              {pharmacy.phones && pharmacy.phones.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {pharmacy.phones.map((phone, pIdx) => (
                                    <a
                                      key={pIdx}
                                      href={`tel:${phone.replace(/\s/g, '')}`}
                                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#2E7D32]/10 text-[#2E7D32] rounded-full text-sm font-medium hover:bg-[#2E7D32]/20 transition-colors"
                                    >
                                      <Phone className="w-3 h-3" />
                                      {phone}
                                    </a>
                                  ))}
                                </div>
                              )}
                              <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#E8DDD4]">
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] px-2 py-0.5 gap-1 ${
                                    pharmacy.geocoded
                                      ? 'border-[#2E7D32]/40 text-[#2E7D32]'
                                      : 'border-[#8B7355]/30 text-[#8B7355]'
                                  }`}
                                >
                                  {pharmacy.geocoded ? (
                                    <><ShieldCheck className="w-3 h-3" /> GPS vérifié</>
                                  ) : (
                                    <><SearchCheck className="w-3 h-3" /> Recherche par nom</>
                                  )}
                                </Badge>
                                <button
                                  onClick={() => openDirections(pharmacy)}
                                  className="inline-flex items-center gap-1 text-[#C45C26] hover:text-[#A84B1F] font-semibold text-sm"
                                  data-testid="directions-btn"
                                >
                                  <Route className="w-4 h-4" />
                                  Itinéraire
                                </button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* PDF Download Button */}
        {gardeData?.pharmacies?.length > 0 && (
          <Card className="mt-8 bg-gradient-to-r from-[#C45C26]/5 to-[#C45C26]/10 border-[#C45C26]/20">
            <CardContent className="p-4">
              <Button
                onClick={generatePDF}
                variant="ghost"
                className="w-full flex items-center justify-center gap-2 text-[#C45C26] hover:text-[#A84B1F] hover:bg-[#C45C26]/10 font-medium"
              >
                <Download className="w-5 h-5" />
                Télécharger le PDF complet ({gardeData.pharmacies.length} pharmacies)
              </Button>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Footer */}
      <footer className="bg-[#5D4E37] text-white py-8 mt-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <img 
            src={logoUrl} 
            alt="Logo" 
            className="h-12 w-auto mx-auto mb-4 brightness-0 invert opacity-80"
          />
          <p className="text-[#D4C4B0] text-sm">
            Informations mises à jour mensuellement
          </p>
          <p className="text-[#D4C4B0] text-xs mt-2">
            © 2026 Pharmacie Saint Ange Des Amis - Tous droits réservés
          </p>
        </div>
      </footer>

      {/* Directions confirmation dialog (shown when no GPS coords) */}
      <AlertDialog
        open={!!directionsWarning}
        onOpenChange={(open) => { if (!open) setDirectionsWarning(null); }}
      >
        <AlertDialogContent className="max-w-md" data-testid="directions-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#C45C26] flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.5a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              Position GPS non vérifiée
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5D4E37] text-sm space-y-3 pt-2">
              <span className="block">
                Nous n'avons pas les coordonnées GPS exactes de <strong>{directionsWarning?.name}</strong>.
              </span>
              <span className="block">
                Google Maps va rechercher la pharmacie par son <strong>nom et adresse</strong>. Le résultat peut être <strong>imprécis</strong>.
              </span>
              <span className="block bg-[#FFF8E7] border border-[#D4AF37]/30 rounded-lg p-2.5 text-xs">
                💡 <strong>Conseil</strong> : si le lieu sur Google Maps semble incorrect, appelez directement la pharmacie avant de vous déplacer.
              </span>
              {directionsWarning?.phones?.length > 0 && (
                <span className="flex flex-wrap gap-2 pt-1">
                  {directionsWarning.phones.slice(0, 2).map((phone, i) => (
                    <a
                      key={i}
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#2E7D32] text-white rounded-full text-xs font-semibold"
                    >
                      📞 {phone}
                    </a>
                  ))}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="directions-cancel-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDirections}
              className="bg-[#C45C26] hover:bg-[#A84B1F]"
              data-testid="directions-confirm-btn"
            >
              Ouvrir Google Maps
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PharmacyGardePage;
